import React, { useEffect, useState } from 'react'
import {
    Breadcrumb,
    Button, ButtonToolbar,
    Card,
    Col,
    Container,
    Form, InputGroup, Modal,
    Row,
    Table, ToggleButton, ToggleButtonGroup
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft'
import axios from 'api/index'
import { IAPI_Response } from 'api/types'
import {API_ENDPOINT, IMAGE_BASE_URL} from 'utils/globalConstant'
import moment from "moment"
import PieChart from "components/PieChart"
import {IPcDetail, IApplicationVisited, IWebsiteVisited, IUsername} from './types'
import Loader from 'components/Loader'
import Calendar from "asset/SvgComponent/Calendar";
import Select from "components/ReactSelect";
import './style.css'
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import TimeLine from './Timeline'
import PieChartWhiteIcon from "asset/SvgComponent/PieChartWhiteIcon";
import PieChartIcon from "asset/SvgComponent/PieChartIcon";
import TableListWhiteIcon from "asset/SvgComponent/TableLIstWhiteIcon";
import TableListIcon from "asset/SvgComponent/TableListIcon";
import ScreenshotIcon from "asset/SvgComponent/ScreenshotIcon";

interface PCDetailPageProps {
    pcId: number
    onBack: () => void
    date_selected: string
    trackerId: number
}

const PCDetailPage: React.FC<PCDetailPageProps> = ({ pcId, date_selected, trackerId: initialTrackerId, onBack }) => {
    const [trackerId, setTrackerId] = useState(initialTrackerId)
    const [pcDetail, setPcDetail] = useState<IPcDetail | null>(null)
    const [applications, setApplications] = useState<IApplicationVisited[]>([])
    const [percentage, setPercentage] = useState<number[]>([0])
    const [sites, setSites] = useState<IWebsiteVisited[]>([])
    const [sitesPercentage, setSitesPercentage] = useState<number[]>([0])
    const [screenshots, setScreenshots] = useState<any[]>([])
    const [showLoading, setShowLoading] = useState(false)
    const [activityLevel, setActivityLevel] = useState("")
    const [dateFormat, setDateFormat] = useState("")
    const [timeFormat, setTimeFormat] = useState("")
    const [showAllScreenshots, setShowAllScreenshots] = useState(false);
    const [showAllApplications, setShowAllApplications] = useState(false);
    const [showAllWebsites, setShowAllWebsites] = useState(false);
    const [showAllHistory, setShowAllHistory] = useState(false)
    const [selectedUser, setSelectedUser] = useState<{value: number, label: string}>({value: 0, label: "All Users"})
    const [userOptions, setUserOptions] = useState<{value: number, label: string}[]>([]);
    // Number of columns per row (assuming md={2} means 6 per row)
    const columnsPerRow = 6;
    const initialRows = 2; // Show only 2 rows initially
    const visibleScreenshotItems = showAllScreenshots ? screenshots.length : columnsPerRow * initialRows;
    const visibleApplicationItems = showAllApplications ? applications.length : 5;
    const visibleSiteItems = showAllWebsites ? sites.length : 5;
    const visibleHistory = showAllHistory ? pcDetail?.history.length : 5;
    const [dateFilterCustom, setDateFilterCustom] = useState(false);
    const [dateFilter, setDateFilter] = useState({
        date: moment(date_selected).format("YYYY-MM-DD"),
        time_from: "",
        time_to: ""
    })
    const [showAllApplicationsModal, setShowAllApplicationsModal] = useState(false)
    const handleShowAllApplicationsModal = () => {
        setShowAllApplicationsModal(true)
    }
    const handleCloseAllApplicationsModal = () => {
        setShowAllApplicationsModal(false)
    }
    const [showAllWebsitesModal, setShowWebsitesModal] = useState(false)
    const handleShowAllWebsitesModal = () => {
        setShowWebsitesModal(true)
    }
    const handleCloseAllWebsitesModal = () => {
        setShowWebsitesModal(false)
    }
    const [showAllScreenshotsModal, setShowScreenshotsModal] = useState(false)
    const handleShowAllScreenshotsModal = () => {
        setShowScreenshotsModal(true)
    }
    const handleCloseAllScreenshotsModal = () => {
        setShowScreenshotsModal(false)
    }

    useEffect(() => {
        setDateFormat(localStorage.getItem('date_format') ?? "")
        setTimeFormat(localStorage.getItem('time_format') ?? "")

        const runEveryMinute = () => {
            fetchPCDetails()
            getUsersOnMachine()
        }

        runEveryMinute();

        const interval = setInterval(runEveryMinute, 60000) // Run every 60 seconds

        return () => clearInterval(interval) // Cleanup on unmount
    }, [pcId, date_selected, trackerId, dateFilter, selectedUser])

    const clickableUsername = async (id: number) => {
        setTrackerId(id)
    }



    const getFirstAndLastTrackIn = () => {
        const data = {
            start: "",
            end: ""
        }
        pcDetail?.history.map((his, index) => {
            if(index == 0){
                data.end = his.track_out ?? new Date()
            }

            if((index + 1) == pcDetail?.history.length){
                data.start = his.track_in
            }
        })

        return data;
    }

    // Generate timeline data for the whole day (30-min intervals)
    const generateTimelineData = () => {
        const timeline: { time: string; duration: number }[] = [];

        if (!pcDetail?.history || pcDetail.history.length === 0) return timeline;

        const startAndLast = getFirstAndLastTrackIn(); // Ensure this function gets the correct start and end
        const startOfDay = dayjs(startAndLast.start, "YYYY-MM-DD HH:mm:ss");
        const endOfDay = dayjs(startAndLast.end, "YYYY-MM-DD HH:mm:ss");

        let maxDuration = 1; // Track the max duration for normalization
        let current = startOfDay;
        while (current.isBefore(endOfDay)) {
            const next = current.add(30, "minute");
            const activeSession = pcDetail.history.find(
                (entry: any) =>
                    dayjs(entry.track_in, "YYYY-MM-DD HH:mm:ss").isBefore(next) &&
                    dayjs(entry.track_out, "YYYY-MM-DD HH:mm:ss").isAfter(current)
            );

            let duration = 0;
            if (activeSession) {
                const trackIn = dayjs(activeSession.track_in, "YYYY-MM-DD HH:mm:ss");
                const trackOut = dayjs(activeSession.track_out, "YYYY-MM-DD HH:mm:ss");
                duration = trackOut.diff(trackIn, "minute"); // Get duration in minutes
                maxDuration = Math.max(maxDuration, duration); // Update max duration
            }

            timeline.push({
                time: current.format("hh:mm A"),
                duration: duration,
            });

            current = next;
        }

        // Normalize durations (scale to max of 100 for better visualization)
        return timeline.map((entry) => ({
            ...entry,
            normalizedDuration: (entry.duration / maxDuration) * 100 || 0, // Avoid NaN
        }));
    };
    const timelineData = generateTimelineData();

    const getUsersOnMachine = async () => {
        setShowLoading(true)
        try {
            // Replace with your actual API endpoint
            const response = await axios.get<IAPI_Response<IUsername[]>>(API_ENDPOINT.GET_USERS_ON_MACHINE+"/"+pcId, {
                params: {
                    date_selected: dateFilter.date
                }
            })
            const result = await response

            const users : {value: number, label: string}[] = [];

            users.push({value: 0, label: "All Users"})
            // console.log("Result ", result)
            if (result?.data?.results) {
                result?.data?.results.map((user: any) => {
                    users.push({value: user.id, label: user.username})
                })
            }

            setUserOptions(users)

            setShowLoading(false)
        } catch (error) {
            console.error('Error fetching PC details:', error)
            setShowLoading(false)
        }
    }

    const fetchPCDetails = async () => {
        setShowLoading(true)
        try {
            // Replace with your actual API endpoint
            const response = await axios.get<IAPI_Response<IPcDetail>>(API_ENDPOINT.GET_PC_DETAILS+"/"+pcId, {
                params: {
                    tracker_id: trackerId,
                    date_filter: dateFilter.date,
                    user_id: selectedUser.value
                }
            })
            const result = await response

            if (result?.data?.results) {
                setPcDetail(result.data.results)

                if (result.data.results.applications_visited && result.data.results.applications_visited.length > 0) {
                    setApplications(result.data.results.applications_visited)
                    // setPercentage(result.data.results.applicationPercentages || [])
                }else{
                    setApplications([])
                }

                if (result.data.results.websites_visited && result.data.results.websites_visited.length > 0) {
                    setSites(result.data.results.websites_visited)
                    // setSitesPercentage(result.data.results.sitesPercentages || [])
                }else{
                    setSites([])
                }

                if (result.data.results.screenshots && result.data.results.screenshots.length > 0) {
                    setScreenshots(result.data.results.screenshots)
                }else{
                    setScreenshots([])
                }

                setActivityLevel(`${result.data.results.activityLevel || 0}%`)
            }

            setShowLoading(false)
        } catch (error) {
            console.error('Error fetching PC details:', error)
            setShowLoading(false)
        }
    }

    const getStatusDisplay = (isOnline: boolean) => {
        return (
            <div
                title={isOnline ? "Online" : "Offline"}
                className={`status-indicator ${isOnline ? "online" : "offline"}`}
            ></div>
        )
    }

    return (
        <>
            <Card className="border-0 shadow-sm mb-4">
                <Row style={{marginLeft: '5px'}}>
                    <Col sm={6}>
                        <Button style={{fontSize: '90%'}} variant="transparent" className="text-decoration-none ps-0 text-muted fw-light" onClick={onBack}>
                            <FontAwesomeIcon icon={faArrowLeft} /> Back
                        </Button>
                    </Col>
                    <Col sm={6} className={"d-flex justify-content-end"}>
                        <span style={{fontSize: '90%'}} className="text-decoration-none m-3">
                            <span className={"text-muted fw-light"}>Last Updated: </span><span>{moment(pcDetail?.last_update).format("MMM-DD-YYYY hh:mm:ss A")}</span>
                        </span>
                    </Col>
                </Row>

                {/*<Card.Header className="bg-white">*/}
                {/*    <h5 className="fw-bold mb-0">PC Details</h5>*/}
                {/*</Card.Header>*/}
                <Card.Body>
                    <Row className={'mb-4'}>
                        <Col sm={2}>
                            <Form.Group className='mb-3' controlId='formHorizontalEmail'>
                                <Form.Label className='fw-bold form-labels'>User Accounts</Form.Label>
                                <Select
                                    name="employee_id"
                                    value={selectedUser}
                                    options={userOptions}
                                    onChange={(e: any) => {
                                        console.log("change")
                                        // handleChangeDropdownOverview(e);
                                        setSelectedUser(e)
                                        setTrackerId(0)
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={2}>
                            <Form.Group className='mb-3' controlId='formHorizontalEmail'>
                                <Form.Label className='fw-bold form-labels'>Date</Form.Label>
                                <InputGroup className='mb-2 ' size={'sm'}>
                                    <InputGroup.Text id='basic-addon1' className='text-muted fw-bold bg-white'>
                                        <Calendar/>
                                    </InputGroup.Text>
                                    <Form.Control
                                        size={'sm'}
                                        type='date'
                                        placeholder='Friday, June 03, 2022'
                                        name='date_from'
                                        // min={moment(pcDetail?.track_in).format("YYYY-MM-DD")}
                                        max={moment().format("YYYY-MM-DD")}
                                        value={dateFilter.date}
                                        // disabled={!dateFilterCustom}
                                        onChange={(e: any) => {
                                            // handleChange(e);
                                            // handleSubmit();
                                            setDateFilter(prevState => ({...prevState, date: e.target.value}))
                                            setSelectedUser({value: 0, label: "All Users"})
                                            // getUsersOnMachine(e.target.value)
                                        }}
                                        // onBlur={handleBlur}
                                        // isInvalid={touched.date_from && !!errors.date_from}

                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        {/*<Col sm={2}>*/}
                        {/*    <Form.Group className='mb-3' controlId='formHorizontalEmail'>*/}
                        {/*        <Form.Label className='fw-bold form-labels'>From</Form.Label>*/}
                        {/*        <InputGroup className='mb-2 ' size={'sm'}>*/}
                        {/*            <InputGroup.Text id='basic-addon1' className='text-muted fw-bold bg-white'>*/}
                        {/*                <Calendar/>*/}
                        {/*            </InputGroup.Text>*/}
                        {/*            <Form.Control*/}
                        {/*                size={'sm'}*/}
                        {/*                type='time'*/}
                        {/*                placeholder=''*/}
                        {/*                name='time_from'*/}
                        {/*                disabled={!dateFilterCustom}*/}
                        {/*                value={dateFilter.time_from}*/}
                        {/*                onChange={(e: any) => {*/}
                        {/*                    // handleChange(e);*/}
                        {/*                    // handleSubmit();*/}
                        {/*                    const date = new Date(`1970-01-01T${e.target.value}`);*/}
                        {/*                    const timeIn24HourFormat = date.toLocaleTimeString("en-GB", {*/}
                        {/*                        hour: "2-digit",*/}
                        {/*                        minute: "2-digit",*/}
                        {/*                        second: "2-digit",*/}
                        {/*                    });*/}
                        {/*                    setDateFilter(prevState => ({...prevState, time_from: timeIn24HourFormat}))*/}
                        {/*                }}*/}
                        {/*                // min={moment(pcDetail?.track_in).format("YYYY-MM-DD")}*/}
                        {/*                // max={pcDetail?.track_out ? moment(pcDetail?.track_out).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}*/}
                        {/*                // value={moment(pcDetail?.track_in).format("YYYY-MM-DD")}*/}
                        {/*                // onChange={(e: any) => {*/}
                        {/*                //     handleChange(e);*/}
                        {/*                //     handleSubmit();*/}
                        {/*                //     setDateFilter(e.target.value)*/}
                        {/*                // }}*/}
                        {/*                // onBlur={handleBlur}*/}
                        {/*                // isInvalid={touched.date_from && !!errors.date_from}*/}

                        {/*            />*/}
                        {/*        </InputGroup>*/}
                        {/*    </Form.Group>*/}
                        {/*</Col>*/}
                        {/*<Col sm={2}>*/}
                        {/*    <Form.Group className='mb-3' controlId='formHorizontalEmail'>*/}
                        {/*        <Form.Label className='fw-bold form-labels'>To</Form.Label>*/}
                        {/*        <InputGroup className='mb-2 ' size={'sm'}>*/}
                        {/*            <InputGroup.Text id='basic-addon1' className='text-muted fw-bold bg-white'>*/}
                        {/*                <Calendar/>*/}
                        {/*            </InputGroup.Text>*/}
                        {/*            <Form.Control*/}
                        {/*                size={'sm'}*/}
                        {/*                type='time'*/}
                        {/*                placeholder=''*/}
                        {/*                name='time_to'*/}
                        {/*                disabled={!dateFilterCustom}*/}
                        {/*                value={dateFilter.time_to}*/}
                        {/*                onChange={(e: any) => {*/}
                        {/*                    // handleChange(e);*/}
                        {/*                    // handleSubmit();*/}
                        {/*                    const date = new Date(`1970-01-01T${e.target.value}`);*/}
                        {/*                    const timeIn24HourFormat = date.toLocaleTimeString("en-GB", {*/}
                        {/*                        hour: "2-digit",*/}
                        {/*                        minute: "2-digit",*/}
                        {/*                        second: "2-digit",*/}
                        {/*                    });*/}
                        {/*                    setDateFilter(prevState => ({...prevState, time_to: timeIn24HourFormat}))*/}
                        {/*                }}*/}
                        {/*                // min={moment(pcDetail?.track_in).format("YYYY-MM-DD")}*/}
                        {/*                // max={pcDetail?.track_out ? moment(pcDetail?.track_out).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}*/}
                        {/*                // value={moment(pcDetail?.track_in).format("YYYY-MM-DD")}*/}
                        {/*                // onChange={(e: any) => {*/}
                        {/*                //     handleChange(e);*/}
                        {/*                //     handleSubmit();*/}
                        {/*                //     setDateFilter(e.target.value)*/}
                        {/*                // }}*/}
                        {/*                // onBlur={handleBlur}*/}
                        {/*                // isInvalid={touched.date_from && !!errors.date_from}*/}

                        {/*            />*/}
                        {/*        </InputGroup>*/}
                        {/*    </Form.Group>*/}
                        {/*</Col>*/}
                    </Row>

                    {pcDetail ? (
                        <>
                            <Row className={'mb-4'}>
                                <Col sm={12}>
                                    <Row className={'mb-5'}>
                                        <Col md={6}>
                                            <Row >
                                                <Col sm={4} className="fw-bold">PC Name:</Col>
                                                <Col>{pcDetail.pc_name}</Col>
                                            </Row>
                                            <Row >
                                                <Col sm={4} className="fw-bold">Total Time Online:</Col>
                                                <Col>{pcDetail.total_hours_online} hrs.</Col>
                                            </Row>
                                            <Row>
                                                <Col sm={4} className="fw-bold">Productivity:</Col>
                                                <Col>
                                                    {pcDetail.productivity ? (
                                                        <span style={{
                                                            color: pcDetail.productivity > 75 ? '#82C341' :
                                                                pcDetail.productivity > 50 ? '#ff7043' : '#DA2B5D',
                                                            fontSize: '120%'
                                                        }}>
                                                    {pcDetail.productivity}%
                                                </span>
                                                    ) : 'N/A'}
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col md={6}>
                                            <Row>
                                                <Col sm={4} className="fw-bold">Status:</Col>
                                                <Col className="d-flex align-items-center">

                                                    <span >{getStatusDisplay(pcDetail.is_online)}</span> &nbsp; {!pcDetail.is_online ? "Offline" : "Online"}
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col sm={4} className="fw-bold">Recent User:</Col>
                                                <Col>{pcDetail.username}</Col>
                                            </Row>
                                            {/*<Row>*/}
                                            {/*    <Col sm={4} className="fw-bold">Track In:</Col>*/}
                                            {/*    <Col>*/}
                                            {/*        {moment(pcDetail.track_in).format(dateFormat)}*/}
                                            {/*        {" "}*/}
                                            {/*        {moment(pcDetail.track_in).format(timeFormat)}*/}
                                            {/*    </Col>*/}
                                            {/*</Row>*/}
                                            {/*<Row>*/}
                                            {/*    <Col sm={4} className="fw-bold">Track Out:</Col>*/}
                                            {/*    <Col>*/}
                                            {/*        {pcDetail.track_out ?*/}
                                            {/*            moment(pcDetail.track_out).format(`${dateFormat} ${timeFormat}`) :*/}
                                            {/*            "On going"}*/}
                                            {/*    </Col>*/}
                                            {/*</Row>*/}

                                        </Col>
                                    </Row>
                                </Col>

                            </Row>

                        </>
                    ) : (
                        <p>Loading PC details...</p>
                    )}

                    <Row className={'mb-4'}>
                        <Col md={12} className="mb-4">
                            <Card.Header className="bg-white d-flex align-items-center text-center" style={{ padding: '10px 16px' }}>
                                <h5 className="fw-bold mb-0" style={{ flexGrow: 1, margin: 0 }}>Activity Timeline</h5>
                            </Card.Header>
                            {/* Timeline Chart */}
                            <TimeLine data={pcDetail?.history} />
                        </Col>
                    </Row>

                    <Row className={'mb-4'}>
                        <Col md={6} className="mb-4">
                            <Card.Header className="bg-white d-flex align-items-center text-center" style={{ padding: '10px 16px' }}>
                                <h5 className="fw-bold mb-0" style={{ flexGrow: 1, margin: 0 }}>Applications</h5>
                                <span className="text-muted" onClick={handleShowAllApplicationsModal} style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                                    See All
                                </span>
                            </Card.Header>

                            {/*<PieChart applications={applications} percentage={percentage} activityLevel={activityLevel} />*/}

                            <Table responsive size="sm" className="mt-3">
                                <thead>
                                <tr>
                                    <th className="text-center" style={{width: '10%'}}>#</th>
                                    <th className="text-center" style={{width: '50%'}}>Application</th>
                                    <th className="text-center" style={{width: '20%'}}>Duration</th>
                                    <th className="text-center" style={{width: '20%'}}>%</th>
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    applications.length > 0 ?
                                        applications.slice(0, 5).map((app, index) => (
                                        <tr key={index}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{app.name}</td>
                                            <td className="text-center">{app.total_time_spent == "0h 0m" ? "< 1m" : app.total_time_spent}</td>
                                            <td className={'text-center'}>{app.percentage < 1 ? "< 1" : app.percentage} %</td>
                                        </tr>
                                    ))
                                    : <tr>
                                        <td className={'text-center'} colSpan={4}>No Applications to show.</td>
                                    </tr>
                                }
                                </tbody>
                            </Table>
                            {/*{*/}
                            {/*    applications.length > 5 ?*/}
                            {/*        <div className="text-center">*/}
                            {/*            <button className="btn text-muted" onClick={() => setShowAllApplications(!showAllApplications)}>*/}
                            {/*                {showAllApplications ? "See Less" : "See More"}*/}
                            {/*            </button>*/}
                            {/*        </div>*/}
                            {/*        : ""*/}
                            {/*}*/}

                        </Col>

                        <Col md={6} className="mb-4">
                            <Card.Header className="bg-white d-flex align-items-center text-center" style={{ padding: '10px 16px' }}>
                                <h5 className="fw-bold mb-0" style={{ flexGrow: 1, margin: 0 }}>Websites</h5>
                                <span className="text-muted" onClick={handleShowAllWebsitesModal} style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                                    See All
                                </span>
                            </Card.Header>
                            {/*<PieChart applications={sites} percentage={sitesPercentage} activityLevel={activityLevel} />*/}

                            <Table responsive size="sm" className="mt-3">
                                <thead>
                                <tr>
                                    <th className="text-center" style={{width: '10%'}}>#</th>
                                    <th className="text-center" style={{width: '50%'}}>Website</th>
                                    <th className="text-center" style={{width: '20%'}}>Duration</th>
                                    <th className="text-center" style={{width: '20%'}}>%</th>
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    sites.length > 0 ?
                                        sites.slice(0, 5).map((site, index) => (
                                        <tr key={index}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{site.title}</td>
                                            <td className="text-center">{site.total_time_spent == "0h 0m" ? "< 1m" : site.total_time_spent}</td>
                                            <td className={'text-center'}>{site.percentage < 1 ? "< 1" : site.percentage} %</td>
                                        </tr>
                                    ))
                                        : <tr>
                                        <td className={'text-center'} colSpan={4}>No Websites to show.</td>
                                        </tr>
                                }
                                </tbody>
                            </Table>
                            {/*{*/}
                            {/*    sites.length > 5 ?*/}
                            {/*        <div className="text-center">*/}
                            {/*            <button className="btn text-muted" onClick={() => setShowAllWebsites(!showAllWebsites)}>*/}
                            {/*                {showAllWebsites ? "See Less" : "See More"}*/}
                            {/*            </button>*/}
                            {/*        </div>*/}
                            {/*        : ""*/}
                            {/*}*/}
                        </Col>
                    </Row>

                    <Row  className={'mb-4'}>
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white d-flex align-items-center text-center" style={{ padding: '10px 16px' }}>
                                <h5 className="fw-bold mb-0" style={{ flexGrow: 1, margin: 0 }}>Screenshots</h5>
                                <span className="text-muted" onClick={handleShowAllScreenshotsModal} style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                                    See All
                                </span>
                            </Card.Header>
                            <Card.Body>
                                {screenshots.length > 0 ?
                                    <>
                                        <Row>
                                            {screenshots.slice(0, 12).map((screenshot, index) => (
                                                <Col key={index} md={2} className="mb-3">
                                                    <a href={IMAGE_BASE_URL + screenshot.path} target="_blank" className="photo-a" rel="noreferrer">
                                                        <Card className="h-100">
                                                            <Card.Img variant="top" src={IMAGE_BASE_URL + screenshot.path} style={{ height: "150px", objectFit: "cover" }} />
                                                            <Card.Body className="p-2" style={{ fontSize: '80%' }}>
                                                                <small className="text-muted" title={screenshot.title}>
                                                                    {moment(screenshot.created_at).format(timeFormat)} {screenshot.title}
                                                                </small>
                                                            </Card.Body>
                                                        </Card>
                                                    </a>
                                                </Col>
                                            ))}
                                        </Row>
                                        {/* See More / See Less Button */}
                                        {/*{*/}
                                        {/*    screenshots.length > 12 ?*/}
                                        {/*        <div className="text-center mt-3">*/}
                                        {/*            <button className="btn text-muted" onClick={() => setShowAllScreenshots(!showAllScreenshots)}>*/}
                                        {/*                {showAllScreenshots ? "See Less" : "See More"}*/}
                                        {/*            </button>*/}
                                        {/*        </div>*/}
                                        {/*        : ""*/}
                                        {/*}*/}

                                    </>
                                    : <Table responsive size="sm">
                                        <tbody>
                                        <tr>
                                            <td className={'text-center'}>No Screenshots to show.</td>
                                        </tr>
                                        </tbody>
                                    </Table>
                                }
                            </Card.Body>
                        </Card>
                    </Row>

                    <Row className={'mb-4'}>
                        <Col md={12} className="mb-4">
                            <Card.Header className="bg-white d-flex align-items-center text-center" style={{ padding: '10px 16px' }}>
                                <h5 className="fw-bold mb-0" style={{ flexGrow: 1, margin: 0 }}>Login History</h5>
                                {/*<span className="text-muted" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>*/}
                                {/*    See All*/}
                                {/*</span>*/}
                            </Card.Header>
                            <Table responsive size="sm" className="mt-3">
                                <thead>
                                <tr>
                                    <th className="text-center" style={{width: '10%'}}>#</th>
                                    <th className="text-center" style={{width: '60%'}}>User Account</th>
                                    <th className="text-center" style={{width: '30%'}}>Duration</th>
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    pcDetail?.history && pcDetail.history.length > 0 ?
                                        pcDetail?.history.slice(0, visibleHistory).map((his, index) => (
                                            <tr key={index}>
                                                <td className={'text-center'}>{index + 1}</td>
                                                <td onClick={() => clickableUsername(his.id)} className={(trackerId == his.id ? "current-username" : "") + ' text-center clickable-username'}>{his.username}</td>
                                                <td onClick={() => clickableUsername(his.id)} className={(trackerId == his.id ? "current-username" : "") + ' text-center clickable-username'}>{moment(his.track_in).format("MMM-DD "+ localStorage.getItem('time_format') ?? "")} - {his.track_out ? moment(his.track_out).format("MMM-DD "+ localStorage.getItem('time_format') ?? "") : "On going"}</td>
                                            </tr>
                                        )) : <tr ><td className={'text-center'} colSpan={3}>No History to show.</td></tr>
                                }

                                </tbody>
                            </Table>
                            {
                                pcDetail?.history && pcDetail.history.length > 5 ?
                                    <div className="text-center">
                                        <button className="btn text-muted" onClick={() => setShowAllHistory(!showAllHistory)}>
                                            {showAllHistory ? "See Less" : "See More"}
                                        </button>
                                    </div>
                                    : ""
                            }


                        </Col>
                    </Row>


                </Card.Body>
            </Card>

            <Modal className='modal' size="lg" centered show={showAllApplicationsModal} onHide={handleCloseAllApplicationsModal}>
                <Modal.Header closeButton >
                    {/*<Modal.Title className='fw-bold text-center'>Activity Time and Screenshots</Modal.Title>*/}
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col sm={12} className={'text-center'}>
                            <p className={'fw-bold'}>List of Applications</p>
                        </Col>
                    </Row>
                    <Table responsive size="sm" className="mt-3">
                        <thead>
                        <tr>
                            <th className="text-center" style={{width: '10%'}}>#</th>
                            <th className="text-center" style={{width: '50%'}}>Application</th>
                            <th className="text-center" style={{width: '20%'}}>Duration</th>
                            <th className="text-center" style={{width: '20%'}}>%</th>
                        </tr>
                        </thead>
                        <tbody>
                        {
                            applications.length > 0 ?
                                applications.map((app, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>{app.name}</td>
                                        <td className="text-center">{app.total_time_spent == "0h 0m" ? "< 1m" : app.total_time_spent}</td>
                                        <td className={'text-center'}>{app.percentage < 1 ? "< 1" : app.percentage} %</td>
                                    </tr>
                                ))
                                : <tr>
                                    <td className={'text-center'} colSpan={4}>No Applications to show.</td>
                                </tr>
                        }
                        </tbody>
                    </Table>

                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={handleCloseAllApplicationsModal}
                        variant='primary text-white'
                        size={'sm'}
                        className='mx-2'
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>


            <Modal className='modal' size="lg" centered show={showAllWebsitesModal} onHide={handleCloseAllWebsitesModal}>
                <Modal.Header closeButton >
                    {/*<Modal.Title className='fw-bold text-center'>Activity Time and Screenshots</Modal.Title>*/}
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col sm={12} className={'text-center'}>
                            <p className={'fw-bold'}>List of Websites</p>
                        </Col>
                    </Row>
                    <Table responsive size="sm" className="mt-3">
                        <thead>
                        <tr>
                            <th className="text-center" style={{width: '10%'}}>#</th>
                            <th className="text-center" style={{width: '50%'}}>Website</th>
                            <th className="text-center" style={{width: '20%'}}>Duration</th>
                            <th className="text-center" style={{width: '20%'}}>%</th>
                        </tr>
                        </thead>
                        <tbody>
                        {
                            sites.length > 0 ?
                                sites.map((site, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>{site.title}</td>
                                        <td className="text-center">{site.total_time_spent == "0h 0m" ? "< 1m" : site.total_time_spent}</td>
                                        <td className={'text-center'}>{site.percentage < 1 ? "< 1" : site.percentage} %</td>
                                    </tr>
                                ))
                                : <tr>
                                    <td className={'text-center'} colSpan={4}>No Websites to show.</td>
                                </tr>
                        }
                        </tbody>
                    </Table>

                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={handleCloseAllWebsitesModal}
                        variant='primary text-white'
                        size={'sm'}
                        className='mx-2'
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal className='modal' size="lg" centered show={showAllScreenshotsModal} onHide={handleCloseAllScreenshotsModal}>
                <Modal.Header closeButton >
                    {/*<Modal.Title className='fw-bold text-center'>Activity Time and Screenshots</Modal.Title>*/}
                </Modal.Header>
                <Modal.Body>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white d-flex align-items-center text-center" style={{ padding: '10px 16px' }}>
                            <h5 className="fw-bold mb-0" style={{ flexGrow: 1, margin: 0 }}>Screenshots</h5>
                            <span className="text-muted" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                                    See All
                                </span>
                        </Card.Header>
                        <Card.Body>
                            {screenshots.length > 0 ?
                                <>
                                    <Row>
                                        {screenshots.map((screenshot, index) => (
                                            <Col key={index} md={3} className="mb-3">
                                                <a href={IMAGE_BASE_URL + screenshot.path} target="_blank" className="photo-a" rel="noreferrer">
                                                    <Card className="h-100">
                                                        <Card.Img variant="top" src={IMAGE_BASE_URL + screenshot.path} style={{ height: "150px", objectFit: "cover" }} />
                                                        <Card.Body className="p-2" style={{ fontSize: '80%' }}>
                                                            <small className="text-muted" title={screenshot.title}>
                                                                {moment(screenshot.created_at).format(timeFormat)} {screenshot.title}
                                                            </small>
                                                        </Card.Body>
                                                    </Card>
                                                </a>
                                            </Col>
                                        ))}
                                    </Row>
                                    {/* See More / See Less Button */}
                                    {/*{*/}
                                    {/*    screenshots.length > 12 ?*/}
                                    {/*        <div className="text-center mt-3">*/}
                                    {/*            <button className="btn text-muted" onClick={() => setShowAllScreenshots(!showAllScreenshots)}>*/}
                                    {/*                {showAllScreenshots ? "See Less" : "See More"}*/}
                                    {/*            </button>*/}
                                    {/*        </div>*/}
                                    {/*        : ""*/}
                                    {/*}*/}

                                </>
                                : <Table responsive size="sm">
                                    <tbody>
                                    <tr>
                                        <td className={'text-center'}>No Screenshots to show.</td>
                                    </tr>
                                    </tbody>
                                </Table>
                            }
                        </Card.Body>
                    </Card>

                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={handleCloseAllScreenshotsModal}
                        variant='primary text-white'
                        size={'sm'}
                        className='mx-2'
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/*<Loader show={showLoading} />*/}
        </>
    )
}

export default PCDetailPage